import {SimplifiedUser} from "@t/user/SimplifiedUser.ts";
import {SuggestedUserParty} from "@t/fd/editor/UserSelect.ts";
import {SimplifiedGroup} from "@t/user/SimplifiedGroup.ts";
import {FormEnvironment} from "@fd/Types.ts";
import {computed, ref, type ComputedRef} from "vue";
import {UserList} from "@t/util/CommonTypes.ts";
import type {PartyName} from "@t/customer/PartyName.ts";
import {storeToRefs} from "pinia";
import {usePartyMapStore} from "@fd/editors/user-select/PartyMapStore.ts";

export type UserPartyKind = "user" | "group" | "party" | "system"
	| "loading" /* the kind isn't known yet (being loaded) */
	| "missing" /* the party wasn' found on the backend */;

export interface USegment {
	key: string;
	type: "party" | "query";
	index: number;
}

export interface DualValue {
	/** Party can be specified by login name or by numeric ID */
	id?: number;	// party ID
	login?: string;	// party login
}

export interface PartySegment extends USegment, DualValue {
	type: "party";

	storeAs: number | string;	// value in data array

	kind: UserPartyKind;
	/** Label displayed on the tag */
	name: string;
	avatar?: string;
	icon?: string;

	/** Title diplayed on mouse over the tag */
	title?: string;
	selected?: boolean;
}

export interface QuerySegment extends USegment {
	type: "query",
	/** This query is the last editor following all tags */
	last?: boolean;
	query: string
}

export interface SegmentWrapPair {
	key: string;
	q?: QuerySegment;
	p?: PartySegment;
}


export function usePartyTags(env:FormEnvironment, array:ComputedRef<UserList>, sys?:ComputedRef<SuggestedUserParty[]|null|undefined>) {

	const { partyMap } = storeToRefs(usePartyMapStore());

	// TODO Because system suggestions are share across the page, they should be placed in local party map and not to the shared partyMap from the store

	const partyTags = computed<PartySegment[]>(() => {
		const ps:PartySegment[] = [];
		const needToLoad = new Set<string | number>();
		for (let i = 0; i < array.value.length; i++) {
			const val = array.value[i];
			let knownValue = partyMap.value[val];
			if (!knownValue) {
				const toLoadValue:PartySegment = { key: val.toString(), type:"party", storeAs: val, kind: "loading", name: env.msg('being.loaded')+"…", index:i };
				if (typeof val === "string") toLoadValue.login = val;
				else toLoadValue.id = val;
				partyMap.value[val] = toLoadValue;
				knownValue = partyMap.value[val];
				needToLoad.add(val);
			}
			ps.push(knownValue);
		}
		if (needToLoad.size > 0) {
			loadTheseParties(needToLoad);
		}
		return ps;
	});

	function asNumber(val:string|number):number|null {
		if (typeof val === "number") return val;
		if (val.length === 0) return null;
		for (let i = 0; i < val.length; i++) {
			const ch = val.charAt(i);
			if (i === 0 && ch === '-') continue;
			if (ch < '0' || ch > '9') return null;
		}
		return parseInt(val);
	}


	async function loadTheseParties(pts: Set<string | number>):Promise<any> {
		// Process system choices
		let nonSystem = new Set<string|number>();
		if (Array.isArray(sys?.value) && sys.value.length > 0) {
			pts.forEach((val) => {
				var n = asNumber(val);
				if (n != null && n < 0 && n > -100000) {
					// This is system value
					for (let s of sys.value) {
						if (n === s.id) {
							insertSystem(n, s);
							return;
						}
					}
				}
				nonSystem.add(val);
			});
		} else nonSystem = pts;

		// Find users
		const users = await env.users();
		const nonUsers = new Set<string|number>();
		nonSystem.forEach((val) => {
			for (let user of users) {
				if (typeof val === "string") {
					if (val === user.login) {
						insertUser(val, user);
						return;
					}
				} else {
					if (val === user.id) {
						insertUser(val, user);
						return;
					}
				}
			}
			// This one wasn't found
			nonUsers.add(val);
		});
		if (nonUsers.size > 0) {
			const groups = await env.groups();
			const nonGroups = new Set<string|number>();
			nonUsers.forEach((val) => {
				for (let grp of groups) {
					if (typeof val === "string") {
						if (val === grp.login) {
							insertGroup(val, grp);
							return;
						}
					} else {
						if (val === grp.id) {
							insertGroup(val, grp);
							return;
						}
					}
				}
				// This one wasn't found
				nonGroups.add(val);
			});
			if (nonGroups.size > 0) {
				// Remaining IDs belongs to regular parties
				const partyNames = await env.parties([...nonGroups]);
				const missing = new Set<string|number>();
				nonGroups.forEach((val) => {
					for (let pn of partyNames) {
						if (typeof val === "string") {
							if (val === pn.login) {
								insertParty(val, pn);
								return;
							}
						} else {
							if (val.toString() === pn.value) {
								insertParty(val, pn);
								return;
							}
						}
					}
					missing.add(val);
				});
				if (missing.size > 0) {
					missing.forEach((val) => {
						markMissing(val);
					})
				}
			}
		}
	}

	function insertUser(id:string|number, usr:SimplifiedUser):void {
		const ps:PartySegment = partyMap.value[id];
		if (ps) {
			ps.kind = "user";
			ps.name = usr.name;
			ps.title = "@" + usr.login;
			ps.id = usr.id;
			ps.login = usr.login;
			if (usr.avatar) ps.avatar = usr.avatar;
			ps.icon = "user";
		}
	}

	function insertSystem(id:string|number, s:SuggestedUserParty):void {
		const ps:PartySegment = partyMap.value[id];
		if (ps) {
			ps.kind = "system";
			ps.name = s.name;
			ps.id = s.id;
			if (s.avatar) ps.avatar = s.avatar;
			if (s.icon) ps.icon = s.icon;
		}
	}

	function insertGroup(id:string|number, grp:SimplifiedGroup):void {
		const ps:PartySegment = partyMap.value[id];
		if (ps) {
			ps.kind = "group";
			ps.name = grp.name;
			ps.title = "@" + grp.login;
			ps.id = grp.id;
			ps.login = grp.login;
			ps.icon = "user-group";
		}
	}

	function insertParty(id:string|number, p:PartyName):void {
		const ps:PartySegment = partyMap.value[id];
		if (ps) {
			ps.kind = "party";
			ps.name = p.name;
			ps.title = p.login ? "@" + p.login : "#" + p.value;
			ps.id = parseInt(p.value);
			if (p.login) ps.login = p.login;
			if (p.icon) ps.icon = p.icon;
		}
	}

	function markMissing(id:string|number):void {
		const ps:PartySegment = partyMap.value[id];
		if (ps) {
			ps.kind = "missing";
			ps.name = ps.id.toString();
			ps.title = env.msg('record.notFound');
		}
	}

	/**
	 * Return an array of values but normalized to numbers/IDs
	 * All items must be already loaded for it to work.
	 **/
	const normalized = computed<number[]>(() => {
		const n:number[] = [];
		for (let item of array.value) {
			const num = asNumber(item);
			if (num != null) n.push(num);
			else {
				const ps = partyMap.value[item];
				n.push(ps?.id ?? -200000);
			}
		}
		return n;
	});

	/** Number of unique parties in the array */
	const uniqueCount = computed<number>(() => {
		const s = new Set<number>();
		for (let v of normalized.value) {
			if (v > -200000) s.add(v);
		}
		return s.size;
	});


	/** Normalize pair ID/login to ID. Returns negative value for unknown logins */
	function norm(id?:number, login?:string):number {
		if (typeof id === "number") return id;
		if (login) {
			const ps = partyMap.value[login];
			return ps?.id ?? -200000;
		}
		return -300000;
	}

	/** Return true if id/login alrady inserted in data */
	function contains(id?:number, login?:string):boolean {
		const nid = norm(id, login);
		if (nid < -100000) return false;
		for (let item of normalized.value) {
			if (item === nid) return true;
		}
		return false;
	}

	return { partyTags, partyMap, normalized, uniqueCount, norm, contains };
}